import { useMemo, useCallback } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { useSetAtom } from 'jotai';
import { DatasourceSummary } from '@/types/api';
import { useGetDatasource } from '@/hooks/core/useGetDataSource';
import { useGetDatasourcePresetVersions } from '@/hooks/content/useGetDatasourcePresetVersions';
import { getAvailablePresetVersion } from '@/lib/datasource/upgrade/versions/datasourceUpgradeVersions';
import { dataSourceUpgradeDialogAtom } from '@/atoms/dataSources/dataSourcesAtoms';

interface DataSourceUpgradeProps {
  summaryWithAlertsAndMetrics: DatasourceSummary;
  iconOnly?: boolean;
  'data-testid'?: string;
}

export const useDataSourceHasUpgrade = (dataSource: DatasourceSummary) => {
  const { enablePresetVersioning } = useFlags<FlagSet>();

  const { data: datasource } = useGetDatasource({
    name: dataSource?.name || '',
  });

  const { data: presetVersions } = useGetDatasourcePresetVersions({
    name: enablePresetVersioning ? datasource?.spec.usePreset || '' : undefined,
  });

  const { state: upgradeState } = useMemo(() => {
    if (!enablePresetVersioning) {
      return { version: undefined, state: 'up-to-date' };
    }
    return getAvailablePresetVersion(datasource, presetVersions);
  }, [datasource, presetVersions, enablePresetVersioning]);

  return upgradeState === 'available' || upgradeState === 'in-progress';
};

// This component still renders alot since this data is fetched in all the time. I don't think disabling refetchOnMount is a good idea.
// If we disable that, we may have stale data in the UI.
export const DataSourceUpgrade = ({
  summaryWithAlertsAndMetrics,
  iconOnly = false,
  'data-testid': testId,
}: DataSourceUpgradeProps) => {
  const { enablePresetVersioning } = useFlags<FlagSet>();
  const setUpgradeDialog = useSetAtom(dataSourceUpgradeDialogAtom);

  const { data: datasource } = useGetDatasource({
    name: summaryWithAlertsAndMetrics?.name || '',
  });

  const { data: presetVersions } = useGetDatasourcePresetVersions({
    name: enablePresetVersioning ? datasource?.spec.usePreset || '' : undefined,
  });

  const { version: latestPresetVersion, state: upgradeState } = useMemo(() => {
    if (!enablePresetVersioning) {
      return { version: undefined, state: 'up-to-date' };
    }
    return getAvailablePresetVersion(datasource, presetVersions);
  }, [datasource, presetVersions, enablePresetVersioning]);

  const showUpgradeButton = useMemo(
    () => upgradeState === 'available' || upgradeState === 'in-progress',
    [upgradeState],
  );

  const handleUpgradeDataSource = useCallback(() => {
    if (!datasource || upgradeState === 'up-to-date') {
      throw new Error(
        `Source: "${datasource?.metadata?.name}" is not available or upgrade version is already up to date`,
      );
    }
    setUpgradeDialog({
      dataSourceName: summaryWithAlertsAndMetrics.name || '',
      upgradePresetVersion: latestPresetVersion?.toString() || null,
    });
  }, [
    datasource,
    upgradeState,
    summaryWithAlertsAndMetrics.name,
    latestPresetVersion,
    setUpgradeDialog,
  ]);

  if (!showUpgradeButton) {
    return null;
  }

  return (
    <>
      <Tooltip
        side="top"
        content={
          upgradeState === 'available'
            ? 'New preset version available'
            : 'Continue preset upgrade'
        }
      >
        <Button
          iconOnly={iconOnly}
          leftIcon="radix-icons:update"
          variant="warning"
          className={iconOnly ? '' : 'ml-1'}
          data-testid={testId}
          onClick={handleUpgradeDataSource}
        />
      </Tooltip>
    </>
  );
};
